import React, { Fragment, useState, useEffect, useRef, useMemo } from "react";
import { useOutletContext} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import AllocationDetailsTable from "../../components/AllocationDetailsTable.jsx";
import dayjs from "dayjs";
import { Filter } from "../../components/form-components/Filter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useKeycloak } from "@react-keycloak/web";
import { useFilterStoreWithMomentDates } from "../../store/filterStore.js";


const API_URL = process.env.REACT_APP_API_URL;

const AllocationDetails = () => {
    const [filters, ] = useState([]);
    const { keycloak, initialized } = useKeycloak();
    // Memoize filterOptions to prevent unnecessary object recreation
    const filterOptions = useMemo(() => ({
		keys: [
			{ name: "label", weight: 0.7 },
		],
		valueKey: "key",
		maxPatternLength: 8,
		includeScore: true,
		maxResults: 25,
		threshold: 0.3,
	}), []);
    
    // Use Zustand store for date state with moment dates
    const { startDate,  endDate } = useFilterStoreWithMomentDates();
    const { selectedOrganisation, setSelectedOrganisation } = useOutletContext();

    // initialization for the variables for the fuzzy search, the filters and the grouping
	const filtersRef = useRef();
	filtersRef.current = filters;

    /**
     * Fetches organizations data from the API
     * 
     * @param {string} token - JWT token for authentication
     * @returns {Promise<Array>} Promise that resolves to array of organization objects
     */
    const fetchOrganisations = async (token) => {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append("Authorization", "Bearer " + token);

        const response = await fetch(API_URL + "/get-organisations", {
            method: "GET",
            headers: headers,
        });
        
        const json = await response.json();
        return prepareOrganisations(json.Organisations);
    };

    // React Query hook for fetching organizations
    const { data: organisations = [] } = useQuery({
        queryKey: ['organisations', keycloak.token],
        queryFn: () => fetchOrganisations(keycloak.token),
        enabled: !!keycloak.token && initialized,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
    });

    /**
     * Fetches cost allocation details from the API and processes the data
     * 
     * @param {Object} params - Object containing token, organisationID, startDate, and endDate
     * @returns {Promise<Object>} Promise that resolves to object with costAllocationDetails and reportingMonths
     */
    const fetchCostAllocationDetails = async ({ token, organisationID, startDate, endDate }) => {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append("Authorization", "Bearer " + token);

        const response = await fetch(
            `${API_URL}/get-cost-allocation-details?organisationID=${organisationID}&startDate=${dayjs(startDate).format("YYYY-MM-DD")}&endDate=${dayjs(endDate).format("YYYY-MM-DD")}`,
            {
                method: "GET",
                headers: headers,
            }
        );
        
        const json = await response.json();
        
        if (json.CostAllocationDetails !== null) {
            const tmpReportingMonths = determineReportingMonths(json.CostAllocationDetails, "month");
            const tmpCostAllocationDetails = addTotalColumn(pivotData(json.CostAllocationDetails, tmpReportingMonths));
            const totalRow = getTotalRow(tmpCostAllocationDetails, tmpReportingMonths);
            tmpCostAllocationDetails.unshift(totalRow);
            
            return {
                costAllocationDetails: tmpCostAllocationDetails,
                reportingMonths: tmpReportingMonths
            };
        } else {
            return {
                costAllocationDetails: [],
                reportingMonths: []
            };
        }
    };

    // React Query hook for fetching cost allocation details
    const { 
        data: costAllocationData = { costAllocationDetails: [], reportingMonths: [] } 
    } = useQuery({
        queryKey: ['costAllocationDetails', keycloak.token, selectedOrganisation?.value, startDate, endDate],
        queryFn: () => fetchCostAllocationDetails({
            token: keycloak.token,
            organisationID: selectedOrganisation?.value,
            startDate,
            endDate
        }),
        enabled: !!keycloak.token && initialized && selectedOrganisation?.value >= 0,
        staleTime: 2 * 60 * 1000, // 2 minutes
        cacheTime: 5 * 60 * 1000, // 5 minutes
    });

    const { costAllocationDetails, reportingMonths } = costAllocationData;





    /**
     * Transforms organization data into a format suitable for dropdown/select components
     * 
     * This function maps each organization element to an object with standardized fields:
     * - category: The organization level description
     * - category_description: The organization level description (duplicate for consistency)
     * - label: The organization name to display in UI
     * - value: The organization name to use as the select value
     * 
     * @param {Array} data - Array of organization objects containing level_description and name
     * @returns {Array} Array of transformed organization objects with standardized fields
     */
    const prepareOrganisations = (data) => {
        return data
        .filter(element => element.level > 0)
        .sort((a, b) => {
            if (a.level !== b.level) {
                return a.level - b.level;
            }
            return a.name.localeCompare(b.name);
        })
        .map((element) => {
            return {
                category: element.level_description,
                category_description: element.level_description,
                label: element.name,
                value: element.id
            }
        });
    }

    /**
     * Determines the list of reporting months between the earliest and latest dates in the data
     * 
     * This function:
     * 1. Takes an array of data objects containing dates and a granularity parameter
     * 2. For monthly granularity:
     *    - Extracts unique dates from the data
     *    - Finds the min and max dates
     *    - Generates an array of all months between min and max dates
     * 3. Returns array of dates formatted as YYYY-MM-DD strings
     * 
     * @param {Array} data - Array of objects containing date field
     * @param {string} granularity - Time granularity ('month' supported)
     * @returns {Array} Array of date strings in YYYY-MM-DD format representing each month
     */
    function determineReportingMonths(data, granularity) {
        if (granularity === "month") {
            const dates = [...new Set(data.map(row => dayjs(row.date).format("YYYY-MM-DD")))];
            const minDate = new Date(Math.min(...dates.map(d => new Date(d))));
            const maxDate = new Date(Math.max(...dates.map(d => new Date(d))));

            const allMonths = [];
            let currentDate = new Date(minDate);
            
            while (currentDate <= maxDate) {
                allMonths.push(dayjs(currentDate).format("YYYY-MM-DD"));
                currentDate = dayjs(currentDate).add(1, 'month').toDate();
            }

            return allMonths;
        }
    }

    /**
     * Adds a total column to pivoted cost allocation data by summing all cost columns
     * 
     * This function:
     * 1. Takes an array of pivoted cost allocation objects where costs are spread across columns
     * 2. For each row, identifies all columns that start with 'C' (cost columns)
     * 3. Calculates the sum of values in those cost columns
     * 4. Adds a new 'total' property to each row with the calculated sum
     * 
     * @param {Array} pivotedData - Array of pivoted cost allocation objects
     * @returns {Array} Array of objects with original data plus total column
     */
    function addTotalColumn(pivotedData) {
        const totalCosts = pivotedData.map(row => {
            // Get all keys that start with 'C'
            const costColumns = Object.keys(row).filter(key => key.startsWith('C'));
            
            // Calculate sum of all cost columns
            const total = costColumns.reduce((sum, key) => {
                return sum + (row[key] || 0);
            }, 0);

            // Return new object with all existing properties plus total
            return {
                ...row,
                total: total
            };
        });

        return totalCosts;
    }   

    /**
     * Calculates a total row by summing costs across all data rows
     * 
     * This function:
     * 1. Creates a total row object with default "Total" values for text columns
     * 2. Initializes cost columns (C1, C2, etc) and grand total to 0
     * 3. Iterates through the pivoted data rows and sums up:
     *    - Each individual cost column (C1, C2, etc)
     *    - The grand total of all costs
     * 
     * @param {Array} pivotedData - Array of pivoted cost allocation objects
     * @param {Array} reportingMonths - Array of reporting month dates
     * @returns {Object} Total row object with summed costs
     */
    function getTotalRow(pivotedData, reportingMonths) {
        var totalRow = {
            allocation_type: selectedOrganisation.label,
            allocation_method: "", 
            allocated_from: "",
            allocated_percentage: "",
        }

        for (let i = 0; i < reportingMonths.length; i++) {
            totalRow["C" + (i + 1)] = 0;
        }

        totalRow["total"] = 0;

        // Sum up each C column across all rows
        pivotedData.forEach(row => {
            for (let i = 0; i < reportingMonths.length; i++) {
                const columnKey = "C" + (i + 1);
                totalRow[columnKey] += (row[columnKey] || 0);
            }
            totalRow["total"] += (row["total"] || 0);
        });

        return totalRow;
    }

    /**
     * Gets the index of a date within an array of reporting periods
     * 
     * This function:
     * 1. Takes an array of reporting period dates and a target date
     * 2. Formats the target date to match the reporting period format (YYYY-MM-DD)
     * 3. Returns the index where the formatted date matches a reporting period
     * 4. Returns -1 if no match is found
     * 
     * @param {Array} reportingPeriods - Array of reporting period dates in YYYY-MM-DD format
     * @param {string|Date} date - Date to find the index for
     * @returns {number} Index of the matching reporting period, or -1 if not found
     */
    function getReportingPeriodIndex(reportingPeriods, date) {
        return reportingPeriods.findIndex(period => period === dayjs(date).format("YYYY-MM-DD"));
    }
    

    /**
     * Pivots cost allocation data from a row-based format to a column-based format
     * 
     * This function:
     * 1. Takes an array of cost allocation records with allocation_type, allocation_method, 
     *    allocated_from, date and allocated_costs fields
     * 2. Groups the records by unique combinations of allocation_type, allocation_method 
     *    and allocated_from
     * 3. For each group, creates columns for each date containing the allocated_costs
     * 4. Returns an array of objects where each object represents one unique allocation
     *    with costs spread across date-based columns
     * 
     * @param {Array} data - Array of cost allocation records to pivot
     * @returns {Array} Array of pivoted allocation objects with costs as columns
     */
    function pivotData(data, reportingPeriods) {
        // Step 1: Group data by unique keys (allocation_type, allocation_method, allocated_from)
        const grouped = {};
            
        data.forEach(row => {
            const key = `${row.allocation_type}|${row.allocation_method}|${row.allocated_from}|${row.allocated_percentage}`;
            
            if (!grouped[key]) {
                grouped[key] = { 
                    allocation_type: row.allocation_type,
                    allocation_method: row.allocation_method,
                    allocated_from: row.allocated_from,
                    allocated_percentage: row.allocated_percentage
                };
            }
           
            // Add the date as a column with the allocated_costs value
            const dateKey = "C" + (getReportingPeriodIndex(reportingPeriods, row.date) + 1);
            grouped[key][dateKey] = row.allocated_costs;
        });

        // Step 2: Convert grouped object back to an array
        return Object.values(grouped);
    }


      

    /**
     * Effect hook to set the selected organization when organizations data is loaded
     * 
     * This effect runs when organizations data is loaded and sets the first organization
     * as selected if no organization is currently selected.
     */
    useEffect(() => {
        if (organisations.length > 0 && selectedOrganisation === '') {
            setSelectedOrganisation(organisations[0]);
        }
    }, [organisations, selectedOrganisation, setSelectedOrganisation]);

    return (
        <Fragment>
            <AdministrationHeader
				title="Organisational Overview"
				className="text-text-3"
                icon={icon({name: "folder-tree", style: "light"})}
			/>
            <div className="pl-6 text-left flex text-text-2 mt-6">
                <div className="min-w-[340px] pt-1 pb-2 mr-8 cursor-pointer">
					<div className="ml-6 flex flex-row pl-4 pt-1 border-1 border-solid border-primary bg-primary rounded-full h-[34px]">
						<div className="flex flex-row min-w-full">
							<div className="flex-1">
								<Filter
									options={organisations}
									fuzzyOptions={filterOptions}
									autoCorrect="off"
									spellCheck="off"
									width={340}
									dropdownWidth={340}
									hideButtons={true}
									title="Organisation"
									selectedValues={selectedOrganisation.label}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
									multi={false}
									handleChange={(e) => setSelectedOrganisation({value: e.value, label: e.label})}
									isSearchable={false}
                                    textColor="white"
                                    showCategory={true}
								/>
							</div>

							<div className="flex-none right-0 w-8 mr-6">
								<FontAwesomeIcon
									icon={icon({
										name: "angle-down",
										family: "sharp",
										style: "regular",
									})}
									className="ml-2 pt-1 text-white"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

            <div className="text-left flex flex-auto flex-col ml-10 border-solid border-border">
                <div className="flex flex-row">
                    {/* <KPI
                        title="Total Costs"
                        currentValue={totalCost}
                        trendData={costTrend}
                        format="currency"
                        showChange={false}
                        backgroundColor={"lightblue10"}
                        style="small"
                    /> */}
                </div>

                <div className="bg-card rounded-card border border-card-bd py-2 my-2 h-screen flex flex-auto flex-col">
                    <AllocationDetailsTable 
                        sourceData={costAllocationDetails} 
                        reportingPeriods={reportingMonths}
                    />
                </div>
            </div>
        </Fragment>
    );
};

export default AllocationDetails;

